import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, Select, Space, message, Alert } from 'antd';
import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// 预定义的标签选项
const TAG_OPTIONS = ['日常', '心情', '思考', '工作', '旅行', '美食', '读书', '学习', '健康', '计划'];

// 获取文章数据（检查权限）
const getPostForEdit = (postId) => {
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  let targetPost = null;
  let isAuthor = false;
  let hasPermission = false;
  
  // 搜索所有用户的文章
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    
    if (key.startsWith('userPosts_')) {
      const userId = key.replace('userPosts_', '');
      const userPosts = JSON.parse(localStorage.getItem(key) || '[]');
      const foundPost = userPosts.find(p => p.id === parseInt(postId));
      
      if (foundPost) {
        targetPost = foundPost;
        isAuthor = currentUser.id === parseInt(userId);
        hasPermission = currentUser.isAdmin || isAuthor;
        break;
      }
    }
  }
  
  return { post: targetPost, isAuthor, hasPermission };
};

const WritePost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAuthor, setIsAuthor] = useState(true);
  const [currentUser, setCurrentUser] = useState({});

  useEffect(() => {
    // 获取当前用户
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    setCurrentUser(user);
    
    if (id) {
      // 编辑模式，加载文章数据并检查权限
      const { post, isAuthor: authorCheck, hasPermission } = getPostForEdit(parseInt(id));
      
      if (!hasPermission) {
        message.error('没有权限编辑此文章！');
        navigate('/');
        return;
      }
      
      if (post) {
        setIsEditing(true);
        setIsAuthor(authorCheck);
        form.setFieldsValue({
          title: post.title,
          content: post.content,
          tags: post.tags || [],
        });
      } else {
        message.error('文章不存在！');
        navigate('/');
      }
    }
  }, [id, form, navigate]);

  const onFinish = (values) => {
    setLoading(true);
    
    // 模拟API调用
    setTimeout(() => {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      
      const postData = {
        id: isEditing ? parseInt(id) : Date.now(),
        title: values.title,
        content: values.content,
        tags: values.tags || [],
        author: {
          id: currentUser.id,
          username: currentUser.username,
          avatar: currentUser.avatar,
          isAdmin: currentUser.isAdmin
        },
        createdAt: isEditing ? undefined : new Date().toISOString(),
        views: 0,
        likes: 0,
        comments: 0,
      };
      
      // 保存文章
      if (isEditing) {
        // 编辑模式：更新文章
        let updated = false;
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          
          if (key.startsWith('userPosts_')) {
            // const userId = key.replace('userPosts_', '');
            const userPosts = JSON.parse(localStorage.getItem(key) || '[]');
            const index = userPosts.findIndex(p => p.id === parseInt(id));
            
            if (index !== -1) {
              // 保持原有的创建时间和统计信息
              const originalPost = userPosts[index];
              userPosts[index] = {
                ...originalPost,
                ...postData,
                createdAt: originalPost.createdAt,
                views: originalPost.views || 0,
                likes: originalPost.likes || 0,
                comments: originalPost.comments || 0,
              };
              
              localStorage.setItem(key, JSON.stringify(userPosts));
              updated = true;
              break;
            }
          }
        }
        
        if (updated) {
          message.success('日记更新成功！');
        } else {
          message.error('更新失败！');
        }
      } else {
        // 新建模式：保存新文章
        const userPosts = JSON.parse(localStorage.getItem(`userPosts_${currentUser.id}`) || '[]');
        const newPost = {
          ...postData,
          id: Date.now(),
          createdAt: new Date().toISOString(),
          views: 0,
          likes: 0,
          comments: 0,
        };
        
        userPosts.unshift(newPost);
        localStorage.setItem(`userPosts_${currentUser.id}`, JSON.stringify(userPosts));
        message.success('日记发布成功！');
      }
      
      setLoading(false);
      navigate(`/post/${postData.id}`);
    }, 500);
  };

  return (
    <div>
      <Space style={{ marginBottom: 20 }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/')}
        >
          返回列表
        </Button>
      </Space>
      
      <Card>
        <Title level={3} style={{ textAlign: 'center', marginBottom: 30 }}>
          {isEditing ? (
            <Space>
              {isAuthor ? '编辑我的日记' : '编辑用户日记'}
              {!isAuthor && currentUser.isAdmin && (
                <span style={{ fontSize: '14px', color: '#ff9ebb' }}>
                  (管理员模式)
                </span>
              )}
            </Space>
          ) : '写日记'}
        </Title>
        
        {isEditing && !isAuthor && (
          <Alert
            message="管理员编辑模式"
            description="您正在以管理员身份编辑其他用户的日记。请谨慎操作。"
            type="warning"
            showIcon
            style={{ marginBottom: 20 }}
          />
        )}
        
        <Form
          form={form}
          onFinish={onFinish}
          layout="vertical"
          initialValues={{ tags: ['日常'] }}
        >
          <Form.Item
            name="title"
            label="标题"
            rules={[
              { required: true, message: '请输入标题！' },
              { min: 2, message: '标题至少2个字符！' },
              { max: 100, message: '标题最多100个字符！' }
            ]}
          >
            <Input 
              placeholder="请输入日记标题" 
              size="large"
              allowClear
            />
          </Form.Item>
          
          <Form.Item
            name="tags"
            label="标签（可选）"
          >
            <Select
              mode="tags"
              size="large"
              placeholder="选择或输入标签"
              style={{ width: '100%' }}
              allowClear
            >
              {TAG_OPTIONS.map(tag => (
                <Option key={tag} value={tag}>
                  {tag}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="content"
            label="内容"
            rules={[
              { required: true, message: '请输入日记内容！' },
              { min: 10, message: '内容至少10个字符！' }
            ]}
          >
            <TextArea 
              placeholder="记录你的心情、思考和日常..."
              rows={16}
              showCount
              maxLength={10000}
              style={{ fontSize: '16px' }}
            />
          </Form.Item>
          
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'center' }}>
              <Button 
                type="primary" 
                htmlType="submit" 
                size="large"
                icon={<SaveOutlined />}
                loading={loading}
                style={{ minWidth: 120 }}
              >
                {isEditing ? '更新日记' : '发布日记'}
              </Button>
              
              <Button 
                size="large"
                onClick={() => form.resetFields()}
              >
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default WritePost;